import { select, DisposalSet, selectAll } from '@neptune/cx/utils';
import { registerFragment } from '@neptune/cx/registry';
import './styles.scss';

const gc = new DisposalSet();

const FOCUS_OUT_DELAY = 150;
const STATE_TOGGLE_TIMEOUT = 300;

// chroa-site-header is persistent shell chrome: it is never re-mounted by a
// Liferay SPA (Senna) navigation, so it never gets a fresh registerFragment
// push to rebind listeners. `disposeOnNavigation: false` on every registration
// here opts out of @neptune/cx's default per-navigation auto-teardown (meant
// for per-page content fragments that DO get a fresh push each navigation) —
// without it, this header's interactive JS would go dead after the first
// in-app navigation with nothing left to rebuild it.
const PERSISTENT = { disposeOnNavigation: false };

registerFragment('chroa-site-header', (fragmentElement, configuration, meta) => {
    gc.disposeAll();
    const openBtn = document.querySelector<HTMLButtonElement>('.chroa-mobile-open');

	if (!openBtn) return;

    gc.register(openBtn, PERSISTENT).addListener('click', () => {
		if (document.body.classList.contains('mobile-nav-open')) {
			document.body.classList.remove('mobile-nav-open');
		} else {
			document.body.classList.add('mobile-nav-open');
		}
	});

    const searchOpenBtn = document.querySelector<HTMLButtonElement>('.chroa-search-open');
    const searchFlyoutWrap = document.querySelector<HTMLDivElement>('.chroa-search-flyout-wrap');
    const searchFlyoutInput = document.querySelector<HTMLInputElement>('.chroa-search-flyout .form-control');

    if (!searchOpenBtn) return;
    gc.register(searchOpenBtn, PERSISTENT).addListener('click', (e) => {
        e.stopPropagation();

        if (document.body.classList.contains('search-flyout-open')) {
            document.body.classList.remove('search-flyout-open');
            searchOpenBtn.setAttribute('aria-expanded', 'false');
        } else {
            document.body.classList.add('search-flyout-open');
            searchOpenBtn.setAttribute('aria-expanded', 'true');
            searchFlyoutInput?.focus();
            function closeSearchFlyout(e) {
                if (e.type == 'click' && !searchFlyoutWrap?.contains(e.target as Node) || e.type == 'keydown' && e.key === 'Escape') {
                    document.body.classList.remove('search-flyout-open');
                    document.removeEventListener('click', closeSearchFlyout);
                    document.removeEventListener('keydown', closeSearchFlyout);
                };
            }
            document.addEventListener('click', closeSearchFlyout);
            document.addEventListener('keydown', closeSearchFlyout);
        }
	});

    // Main menu
    const isEditMode = document.body.classList.contains('has-edit-mode-menu');

    const navItemEls = selectAll<HTMLDivElement>(fragmentElement, '.chroa-header-nav-item');
    navItemEls.forEach(navItemEl => {
        const submenuEl = select<HTMLDivElement>(navItemEl, '.chroa-header-nav-item__submenu');
        const toggleEl = select<HTMLButtonElement>(navItemEl, '.chroa-header-nav-item__toggle');
        // const submenuAccordionEls = selectAll<HTMLDivElement>(submenuEl, '.chroa-accordion');
        let contextPopped = false;
        let innerClicked = false;
        let manualClose = false;
        let toggleClicked = false;

        (gc.register(toggleEl, PERSISTENT)
            .addListener('click', (e) => {
                toggleClicked = true;
                setTimeout(() => { toggleClicked = false; }, STATE_TOGGLE_TIMEOUT);
                const wasOpen = navItemEl.classList.contains('chroa-header-nav-item--open');
                navItemEl.classList.toggle('chroa-header-nav-item--open');
                if(toggleEl.getAttribute('aria-expanded') == 'true') {
                    toggleEl.setAttribute('aria-expanded', 'false');
                } else {
                    toggleEl.setAttribute('aria-expanded', 'true');
                }
                if (wasOpen) {
                    manualClose = true;
                    setTimeout(() => { manualClose = false; }, STATE_TOGGLE_TIMEOUT);
                } else {
                    // submenuAccordionEls.forEach((accordionEl) => {
                    //     const accordion = accordionRegistry.get(accordionEl);
                    //     if (accordion) accordion.resize();
                    // });
                }
            })
        );

        if (!isEditMode) { // make open/close strictly controlled my toggle clicking when in edit mode
            (gc.register(submenuEl, PERSISTENT)
                .addListener('contextmenu', e => {
                    contextPopped = true;
                    setTimeout(() => {
                        contextPopped = false;
                    }, STATE_TOGGLE_TIMEOUT);
                })
                .addListener('focusout', e => {
                    setTimeout(() => {
                        // use timeout here because document.activeElement does not update instantly. Also, focusout triggers
                        // before document.click even when we wrap with rAF. If there is an outside click, we need to wait
                        // until the value of inside clicked is updated
                        if (
                            document.activeElement
                            && !submenuEl.contains(document.activeElement)
                            && !manualClose
                            && !innerClicked
                            && !contextPopped
                        ) {

                            navItemEl.classList.remove('chroa-header-nav-item--open');
                            toggleEl.setAttribute('aria-expanded', 'false');
                        }
                        // console.log(`[${instance}] focusout:  manualClose: ${manualClose}, innerClicked: ${innerClicked}, contextPopped: ${contextPopped}`, document.activeElement);
                    }, FOCUS_OUT_DELAY)
                })
            );
            (gc.register(document, PERSISTENT)
                .addListener('click', e => {
                    if (toggleClicked) return;
                    // console.log(`[${instance}] document.click: innerClick: ${e.target && submenuEl.contains(e.target as Node)} manualClose: ${manualClose}, innerClicked: ${innerClicked}, contextPopped: ${contextPopped}`, document.activeElement, e.target);
                    if (e.target && submenuEl.contains(e.target as Node)) {
                        innerClicked = true;
                        setTimeout(() => {
                            innerClicked = false;
                        }, STATE_TOGGLE_TIMEOUT);
                    } else {
                        navItemEl.classList.remove('chroa-header-nav-item--open');
                        toggleEl.setAttribute('aria-expanded', 'false');
                    }
                })
            );
        }
    });

    const mobileEyebrowItemEls = selectAll<HTMLDivElement>(fragmentElement, '.level-1 > .lfr-nav-item');
    mobileEyebrowItemEls.forEach(navItemEl => {
        if(navItemEl.querySelector('.level-2')) {
            const submenuEl = select<HTMLDivElement>(navItemEl, '.level-2');
            const navItemElTitleEl = select<HTMLDivElement>(navItemEl, '.lfr-nav-item');
            let toggleButton = document.createElement('button');
            toggleButton.classList.add('chroa-header-nav-item__toggle');

            let toggleButtonText = document.createElement('span');
            toggleButtonText.classList.add('sr-only');
            toggleButtonText.innerText = `Open submenu for "${navItemElTitleEl.innerText}"`;

            toggleButton.appendChild(toggleButtonText);

            navItemEl.insertBefore(toggleButton, submenuEl);
            const toggleEl = select<HTMLButtonElement>(navItemEl, '.chroa-header-nav-item__toggle');
            // const submenuAccordionEls = selectAll<HTMLDivElement>(submenuEl, '.chroa-accordion');
            let contextPopped = false;
            let innerClicked = false;
            let manualClose = false;
            let toggleClicked = false;

            (gc.register(toggleEl, PERSISTENT)
                .addListener('click', (e) => {
                    toggleClicked = true;
                    setTimeout(() => { toggleClicked = false; }, STATE_TOGGLE_TIMEOUT);
                    const wasOpen = navItemEl.classList.contains('chroa-header-nav-item--open');
                    navItemEl.classList.toggle('chroa-header-nav-item--open');
                    if(toggleEl.getAttribute('aria-expanded') == 'true') {
                        toggleEl.setAttribute('aria-expanded', 'false');
                    } else {
                        toggleEl.setAttribute('aria-expanded', 'true');
                    }
                    if (wasOpen) {
                        manualClose = true;
                        setTimeout(() => { manualClose = false; }, STATE_TOGGLE_TIMEOUT);
                    } else {
                        // submenuAccordionEls.forEach((accordionEl) => {
                        //     const accordion = accordionRegistry.get(accordionEl);
                        //     if (accordion) accordion.resize();
                        // });
                    }
                })
            );

            if (!isEditMode) { // make open/close strictly controlled my toggle clicking when in edit mode
                (gc.register(submenuEl, PERSISTENT)
                    .addListener('contextmenu', e => {
                        contextPopped = true;
                        setTimeout(() => {
                            contextPopped = false;
                        }, STATE_TOGGLE_TIMEOUT);
                    })
                    .addListener('focusout', e => {
                        setTimeout(() => {
                            // use timeout here because document.activeElement does not update instantly. Also, focusout triggers
                            // before document.click even when we wrap with rAF. If there is an outside click, we need to wait
                            // until the value of inside clicked is updated
                            if (
                                document.activeElement
                                && !submenuEl.contains(document.activeElement)
                                && !manualClose
                                && !innerClicked
                                && !contextPopped
                            ) {

                                navItemEl.classList.remove('chroa-header-nav-item--open');
                                toggleEl.setAttribute('aria-expanded', 'false');
                            }
                            // console.log(`[${instance}] focusout:  manualClose: ${manualClose}, innerClicked: ${innerClicked}, contextPopped: ${contextPopped}`, document.activeElement);
                        }, FOCUS_OUT_DELAY)
                    })
                );
                (gc.register(document, PERSISTENT)
                    .addListener('click', e => {
                        if (toggleClicked) return;
                        // console.log(`[${instance}] document.click: innerClick: ${e.target && submenuEl.contains(e.target as Node)} manualClose: ${manualClose}, innerClicked: ${innerClicked}, contextPopped: ${contextPopped}`, document.activeElement, e.target);
                        if (e.target && submenuEl.contains(e.target as Node)) {
                            innerClicked = true;
                            setTimeout(() => {
                                innerClicked = false;
                            }, STATE_TOGGLE_TIMEOUT);
                        } else {
                            navItemEl.classList.remove('chroa-header-nav-item--open');
                            toggleEl.setAttribute('aria-expanded', 'false');
                        }
                    })
                );
            }
        }
    });
});

if (import.meta.hot) {
    import.meta.hot.accept();
    import.meta.hot.dispose(() => {
        gc.disposeAll();
    });
}
